import { BlogPostTemplate } from '../../components/blog/BlogPostTemplate';
import { Link } from 'react-router-dom';

const p = "text-gray-700 dark:text-gray-300 mb-4";
const h2 = "text-2xl font-semibold mt-10 mb-4 text-gray-900 dark:text-white";
const h3 = "text-lg font-semibold mt-6 mb-2 text-gray-900 dark:text-white";
const ul = "list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300 mb-6";
const th = "border border-gray-200 dark:border-gray-700 px-4 py-2 text-left text-gray-900 dark:text-white font-semibold";
const td = "border border-gray-200 dark:border-gray-700 px-4 py-2 text-gray-700 dark:text-gray-300";
const tr = "odd:bg-white even:bg-gray-50 dark:odd:bg-gray-900 dark:even:bg-gray-800";

function Table({ head, rows }: { head: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto my-6">
      <table className="w-full text-sm border-collapse border border-gray-200 dark:border-gray-700">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800">{head.map(h => <th key={h} className={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => <tr key={i} className={tr}>{r.map((c, j) => <td key={j} className={td}>{c}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

export function CommercialLowVoltageFieldGuidePage() {
  return (
    <BlogPostTemplate
      title="The Commercial Low Voltage Field Guide for Treasure Valley Businesses"
      metaTitle="Commercial Low Voltage Guide: Cabling, Fiber, Cameras, Access Control | Boise & Meridian"
      metaDescription="Plain-English guide to commercial low voltage in Boise and Meridian: Cat6 vs Cat6a, fiber types, MDF/IDF rooms, PoE budgets, cameras, access control door hardware, Wi-Fi and new-build timing."
      category="Network Infrastructure"
      date="November 3, 2026"
      readTime="14 min read"
      path="/blog/commercial-low-voltage-field-guide-boise-meridian"
      keywords={['commercial low voltage contractor Boise', 'low voltage contractor Meridian', 'low voltage wiring commercial building', 'structured cabling guide', 'access control door hardware', 'PoE budget cameras', 'MDF IDF', 'fiber single-mode vs multimode']}
    >
      <div className="prose prose-lg max-w-none">

        <p className="text-lg text-gray-700 dark:text-gray-300 mb-6">
          "Low voltage" is everything in a building that carries data, video or control signals instead of line power: network cabling, fiber,
          security cameras, access control, Wi-Fi, paging and AV. If you are planning an office build-out, a warehouse, a tenant improvement
          or an upgrade in Boise, Meridian, Nampa or Eagle, this guide explains the parts, the numbers and the decisions you will be asked to make,
          so you can read a quote and know what you are paying for.
        </p>

        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 text-blue-900 dark:text-blue-100 p-4 text-sm rounded my-6">
          <strong>Quick answer:</strong> A commercial low voltage contractor designs and installs the cabling and devices that run on power-limited circuits:
          Cat6/Cat6a and fiber, network racks, PoE switches, Wi-Fi access points, security cameras and access control (readers, controllers and electric door hardware).
          Bring them in before drywall on new construction, ask for one plan that covers every system, and insist on tested, labeled and documented cable.
        </div>

        <h2 className={h2}>1. What counts as low voltage</h2>
        <p className={p}>
          Low voltage work covers power-limited circuits, generally 50 volts or less. Line-voltage work (outlets, panels, lighting circuits) belongs to a licensed
          electrician. Many commercial projects need both trades; see our{' '}
          <Link to="/blog/low-voltage-contractor-vs-electrician-boise" className="text-blue-600 hover:underline">low voltage contractor vs. electrician guide</Link>{' '}
          for who does what.
        </p>
        <Table
          head={["System", "What it includes", "Typical cable"]}
          rows={[
            ["Data / structured cabling", "Desk and wall drops, patch panels, racks, labeling", "Cat6 or Cat6a copper"],
            ["Backbone", "Links between network rooms, floors and buildings", "Fiber (sometimes Cat6a for short runs)"],
            ["Wi-Fi", "Access points on ceilings and walls", "Cat6/Cat6a with PoE"],
            ["Security cameras", "IP cameras, NVR, viewing stations", "Cat6 with PoE"],
            ["Access control", "Readers, controllers, electric strikes, maglocks, exit devices", "Composite or multi-conductor cable plus Cat6"],
            ["Voice, paging, AV", "VoIP phones, overhead paging, displays", "Cat6, speaker wire, HDMI-over-IP"],
          ]}
        />

        <h2 className={h2}>2. Copper cabling: Cat6 vs. Cat6a</h2>
        <p className={p}>
          Commercial copper cabling follows the TIA-568 standard. The maximum horizontal channel is 100 meters (328 feet): up to 90 meters of solid cable in the walls
          (the permanent link) plus patch cords at each end. Any device farther than that from its network room needs a closer room, a fiber link or a switch in between.
        </p>
        <Table
          head={["", "Cat6", "Cat6a"]}
          rows={[
            ["Frequency", "250 MHz", "500 MHz"],
            ["10 Gb/s distance", "Up to about 55 m, depending on conditions", "Full 100 m"],
            ["Cable size", "Smaller, easier in tight pathways", "Thicker; fills trays and conduit faster"],
            ["Good fit", "Desks, phones, most cameras in existing offices", "New builds, Wi-Fi 6E/7 access points, long runs, future 10 Gb/s"],
          ]}
        />
        <p className={p}>
          Both Cat6 and Cat6a carry Power over Ethernet, including high-power PoE (802.3bt). For larger PoE bundles, Cat6a and 23 AWG cable run cooler, which matters when
          dozens of powered cables share one pathway. Read our{' '}
          <Link to="/blog/cat6-vs-cat6a-cabling-guide-boise" className="text-blue-600 hover:underline">Cat6 vs. Cat6a guide</Link> for the cost side.
        </p>
        <h3 className={h3}>Plenum vs. riser</h3>
        <p className={p}>
          Cable jackets are rated for where they run. Plenum-rated cable (CMP) is required in air-handling spaces such as many drop-ceiling return plenums.
          Riser cable (CMR) is for vertical runs between floors. Using the wrong jacket is a common reason a building inspector fails a job.
        </p>
        <h3 className={h3}>Pathways and separation</h3>
        <p className={p}>
          Cable should be supported on J-hooks or tray, not laid on ceiling tiles, and kept away from fluorescent ballasts, motors and line-voltage wiring.
          Conduit stubs, low voltage mounting brackets and backboxes go in before drywall, which is why timing matters (section 9).
        </p>

        <h2 className={h2}>3. Fiber: when you need it and which kind</h2>
        <p className={p}>
          Fiber is used when copper's 100-meter limit is not enough, between buildings (no shared electrical ground and no lightning path), for backbone links between
          network rooms, and where there is heavy electrical interference.
        </p>
        <Table
          head={["Type", "Where it fits", "Notes"]}
          rows={[
            ["Single-mode (OS2, yellow jacket)", "Between buildings, long campus runs, carrier hand-offs", "Longest distance; optics have become inexpensive, so it is a common default for new backbone"],
            ["Multimode OM3 / OM4 (aqua)", "Inside one building, room-to-room backbone", "10 Gb/s to about 300 m (OM3) or 400 m (OM4)"],
            ["Multimode OM5 (lime)", "Data center and short high-speed links", "Rarely needed for a typical office"],
          ]}
        />
        <p className={p}>
          The common connector is LC; SC still appears on carrier equipment and older panels. Fiber is terminated by fusion splicing pigtails or with factory pre-terminated
          assemblies, then tested for loss. Outdoor runs need outside-plant rated cable, usually in conduit.
        </p>

        <h2 className={h2}>4. Network rooms: MDF, IDF and backbone</h2>
        <ul className={ul}>
          <li><strong>MDF (main distribution frame):</strong> the main network room where internet service enters and the core switch, firewall and servers or NVR live.</li>
          <li><strong>IDF (intermediate distribution frame):</strong> a smaller room or wall cabinet that serves an area within 90 m of cable. Large floors and warehouses often need one or more.</li>
          <li><strong>Backbone:</strong> fiber (or short Cat6a) from each IDF back to the MDF, usually in a star so one failed link only affects one area.</li>
          <li><strong>What a good room has:</strong> a rack or cabinet with patch panels and cable management, dedicated power and a UPS for PoE switches, ventilation, a lock, and every port labeled to match the floor plan.</li>
        </ul>

        <h2 className={h2}>5. Power over Ethernet budgets</h2>
        <p className={p}>
          Cameras, Wi-Fi access points, phones and many access control readers are powered by the switch. Each switch has a total PoE budget, and each port has a class limit.
        </p>
        <Table
          head={["Standard", "Power at the switch port", "Power at the device", "Typical devices"]}
          rows={[
            ["802.3af (PoE)", "15.4 W", "12.95 W", "Phones, basic cameras, readers"],
            ["802.3at (PoE+)", "30 W", "25.5 W", "Most Wi-Fi access points, PTZ-lite cameras"],
            ["802.3bt Type 3", "60 W", "51 W", "Wi-Fi 7 access points, heated cameras, some door hubs"],
            ["802.3bt Type 4", "90 W", "71.3 W", "Large PTZ cameras with heaters, displays"],
          ]}
        />
        <p className={p}>
          Add up the devices on each switch, leave headroom of roughly 20 to 30 percent, and put the switch on a UPS so cameras and door readers stay up during short power events.
        </p>

        <h2 className={h2}>6. Security cameras</h2>
        <ul className={ul}>
          <li>Each IP camera gets its own home-run cable to a PoE switch; outdoor runs need outdoor-rated cable and a weatherproof junction box at the camera.</li>
          <li>Storage depends on resolution, frame rate, motion versus continuous recording and how many days you must keep; decide retention first, then size the NVR.</li>
          <li>Plan coverage by task: identify faces at entrances, overview in parking lots and warehouses, read plates only where a camera is placed for it.</li>
          <li>Put cameras on their own VLAN so they are not reachable from guest Wi-Fi.</li>
        </ul>

        <h2 className={h2}>7. Access control: the electronic side of the door</h2>
        <p className={p}>A card or fob door has more parts than the reader on the wall:</p>
        <Table
          head={["Part", "What it does"]}
          rows={[
            ["Reader", "Reads the card, fob, phone or PIN"],
            ["Controller / door hub", "Decides who is allowed in, when, and logs every event"],
            ["Electric strike", "Replaces the strike plate in the frame; the existing lever lock stays, and free exit is mechanical"],
            ["Maglock", "An electromagnet that holds the door; needs a request-to-exit device and must release on fire alarm and power loss"],
            ["Request-to-exit (REX)", "Motion sensor or push button that tells the system a person is leaving, so it does not log a forced door"],
            ["Door position switch", "Reports door open, closed, held open or forced"],
            ["Power supply", "Powers locks, often with battery backup"],
          ]}
        />
        <p className={p}>
          <strong>Fail-safe vs. fail-secure:</strong> a fail-safe lock unlocks when power is lost; a fail-secure lock stays locked from the outside but always lets people out.
          Egress and fire code rules apply to every door, and the local building official (the authority having jurisdiction) has the final say, especially on maglocks.
        </p>
        <p className={p}>
          Mechanical keys, cylinders and master key systems are locksmith work. The access control installer handles the readers, controllers, electric strikes, maglocks,
          exit devices and the cabling, and coordinates with your locksmith or door supplier when the hardware itself has to change.
        </p>

        <h2 className={h2}>8. Wi-Fi</h2>
        <ul className={ul}>
          <li>Access points are placed from a site survey or floor-plan model, not by counting rooms. Warehouses with racking and metal need more planning than open offices.</li>
          <li>Every access point should be cabled back to a PoE switch; wireless mesh is a fallback for places cable cannot reach.</li>
          <li>Separate networks (VLANs) for staff, guests, cameras and building systems keep problems contained.</li>
        </ul>

        <h2 className={h2}>9. New construction and tenant improvements: timing</h2>
        <Table
          head={["Phase", "Low voltage work"]}
          rows={[
            ["Design", "Drop counts per room, camera and door locations, network room size and power, pathway routes"],
            ["Rough-in (walls open)", "Backboxes, low voltage brackets, conduit stubs, sleeves through fire walls, cable pulled to rooms"],
            ["After drywall and paint", "Terminate jacks and patch panels, mount cameras, readers and access points, set up the rack"],
            ["Before move-in", "Test and label every cable, program switches, firewall, Wi-Fi, cameras and doors, hand over documentation"],
          ]}
        />
        <p className={p}>
          Missing rough-in is the most expensive mistake on a build-out: fishing cable through finished walls costs far more than pulling it while they are open.
        </p>

        <h2 className={h2}>10. Testing and documentation you should receive</h2>
        <ul className={ul}>
          <li>A test result for every cable run (certification testing for warranty-grade jobs; at minimum a pass/fail verification).</li>
          <li>Labels on both ends of every cable that match a floor plan or spreadsheet.</li>
          <li>Fiber loss test results for every strand.</li>
          <li>Network documentation: switch ports, VLANs, IP addresses and admin access handed to you, not kept only by the installer.</li>
        </ul>

        <h2 className={h2}>11. Questions to ask any low voltage contractor</h2>
        <ul className={ul}>
          <li>Does your own crew pull the cable and run the fiber, or is it subcontracted?</li>
          <li>Can you design cabling, cameras, access control and Wi-Fi as one plan?</li>
          <li>Which camera and access control platforms do you install and support?</li>
          <li>Who handles the electric door hardware, and how do you coordinate with our locksmith or door supplier?</li>
          <li>What testing and documentation do we get at the end?</li>
          <li>Who do we call after the job is done?</li>
        </ul>

        <h2 className={h2}>How XXL IT Services does it</h2>
        <p className={p}>
          XXL IT Services is a commercial low voltage contractor based in Meridian, serving Boise, Meridian, Nampa, Eagle and the rest of the Treasure Valley. Our own crew runs
          the Cat6, Cat6a and fiber, builds the network rack, and installs the switches, firewall and Wi-Fi. We are certified UniFi installers, install UniFi Protect
          cameras, and install access control with UniFi Access and PDK, including the electric door hardware. Commercial work only.
        </p>
        <ul className={ul}>
          <li><Link to="/services/structured-cabling" className="text-blue-600 hover:underline">Structured cabling and fiber</Link></li>
          <li><Link to="/services/network" className="text-blue-600 hover:underline">Business network installation</Link></li>
          <li><Link to="/services/security-cameras" className="text-blue-600 hover:underline">Security cameras</Link></li>
          <li><Link to="/services/access-control" className="text-blue-600 hover:underline">Access control and key card entry</Link></li>
          <li><Link to="/contact" className="text-blue-600 hover:underline">Request a site survey</Link></li>
        </ul>

      </div>
    </BlogPostTemplate>
  );
}

export default CommercialLowVoltageFieldGuidePage;
